import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text, TextLink, useTooltip } from '@frontend/design-system';
import { InlineEditAction } from './inline-fields';

type Props = {
  colId: string;
  isColEditing?: boolean;
  isRowEditing?: boolean;
  onEdit?: (columnId: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
};

export const TableHeader = ({ colId, isColEditing, isRowEditing, onEdit, onSave, onCancel }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({ placement: 'bottom-start' });
  const headerWithInfo = ['SourceId', 'Providers'].includes(colId);
  const headerWithEdit = ['DisplayName', 'LocationId'].includes(colId);

  const { title, tooltipText } = useMemo(() => {
    switch (colId) {
      case 'SourceId':
        return {
          title: t('Source'),
          tooltipText: t('Your integrated system of record'),
        };
      case 'Providers':
        return {
          title: t('Default Providers'),
          tooltipText: t('The provider automatically assigned to appointments booked in this operatory'),
        };
      case 'DisplayName':
        return {
          title: t('Display Name'),
        };
      case 'LocationId':
        return {
          title: t('Location'),
        };
      default:
        return { title: '', tooltipText: '' };
    }
  }, [colId, t]);

  if (headerWithInfo) {
    return (
      <>
        <header css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <Text size='medium' css={{ color: theme.colors.neutral40 }}>
            {title}
          </Text>
          <span {...triggerProps}>
            <Icon name='info-small' />
          </span>
        </header>
        <Tooltip {...tooltipProps}>{tooltipText}</Tooltip>
      </>
    );
  }

  if (headerWithEdit) {
    return (
      <header css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
        <Text size='medium' css={{ color: theme.colors.neutral40 }}>
          {title}
        </Text>
        {isColEditing ? (
          <InlineEditAction onSave={() => onSave?.()} onCancel={() => onCancel?.()} />
        ) : (
          <>
            {!isRowEditing && (
              <TextLink
                size='medium'
                css={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => onEdit?.(colId)}
              >
                {t('Edit')}
              </TextLink>
            )}
          </>
        )}
      </header>
    );
  }

  return null;
};
